import { useEffect, useState } from "react";

/**
 * Reads the active discount-link context from localStorage.
 *
 * When a visitor lands on a /go/<slug> URL, DiscountLanding stores three
 * keys in localStorage:
 *   - discount_link_slug   (e.g. "service-leavers")
 *   - discount_link_name   (e.g. "Service Leavers")
 *   - discount_link_pct    (e.g. "50")
 *
 * Any page that displays pricing (LandingPage teaser, Pricing page, etc.)
 * can call this hook to render strikethrough-original + discounted prices
 * automatically. No prop-drilling. Returns null if no discount is active.
 *
 * applyTo(price) helper does the arithmetic and rounds to .99-ending so
 * prices still look like real prices (£14.99 not £14.995).
 */
export function useDiscountFromLink() {
  const [discount, setDiscount] = useState(() => readFromStorage());

  useEffect(() => {
    // Re-read if another tab updates it (rare but harmless to support)
    const onStorage = (e) => {
      if (e.key && e.key.startsWith("discount_link_")) {
        setDiscount(readFromStorage());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!discount) return null;

  return {
    ...discount,
    /**
     * Apply the discount to a numeric price and format. Returns:
     *   { was: 29.99, now: 14.99, label: "50% off" }
     * Or null if no discount active.
     *
     * Rounding mirrors the backend (server.py uses int() truncation on pence)
     * so the visible price on /pricing matches what gets charged.
     */
    applyTo(originalPrice) {
      if (!originalPrice || originalPrice <= 0) return null;
      // Work in pence then floor, matching backend `int(base_amount * (1 - pct/100))`
      const pence = Math.round(originalPrice * 100);
      const discountedPence = Math.floor(pence * (1 - discount.pct / 100));
      const now = discountedPence / 100;
      return {
        was: originalPrice,
        now,
        label: `${discount.pct}% off`,
      };
    },
  };
}

function readFromStorage() {
  try {
    const slug = localStorage.getItem("discount_link_slug");
    const name = localStorage.getItem("discount_link_name");
    const pctRaw = localStorage.getItem("discount_link_pct");
    if (!slug || !pctRaw) return null;
    const pct = parseInt(pctRaw, 10);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) return null;
    return { slug, name: name || slug, pct };
  } catch {
    return null;
  }
}
